import { Skeleton } from '@/components/ui/skeleton';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SectionCard } from '@/components/section-card';

// Card header with title, value, and description
export function CardHeaderSkeleton() {
  return (
    <div className="flex flex-col gap-2 w-full">
      <Skeleton className="h-6 w-full mb-2" />
      <Skeleton className="h-10 w-2/3 mb-2" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}

// Chart area skeleton
export function ChartSkeleton() {
  return (
    <div className="h-[200px] w-full">
      <Skeleton className="h-full w-full" />
    </div>
  );
}

// Table skeleton with customizable rows and columns
interface TableSkeletonProps {
  rowCount?: number;
  columnCount?: number;
  showIcon?: boolean;
}

export function TableSkeleton({
  rowCount = 3,
  columnCount = 4,
  showIcon = true
}: TableSkeletonProps) {
  return (
    <>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[200px]">
            <Skeleton className="h-4 w-24" />
          </TableHead>
          {Array.from({ length: columnCount }).map((_, i) => (
            <TableHead key={i} className="px-6 whitespace-nowrap">
              <Skeleton className="h-4 w-32" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: rowCount }).map((_, i) => (
          <TableRow key={i}>
            <TableCell className="font-medium w-[200px]">
              <div className="flex items-center space-x-2">
                {showIcon && <Skeleton className="h-8 w-8 rounded-full" />}
                <Skeleton className="h-4 w-24" />
              </div>
            </TableCell>
            {Array.from({ length: columnCount }).map((_, j) => (
              <TableCell key={j} className="text-center">
                <Skeleton className="h-4 w-12 mx-auto" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </>
  );
}

export function AudienceStepSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column - Audience Selection */}
        <div className="space-y-6">
          <SectionCard title="Audience">
            {/* Send to section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
            </div>

            {/* Don't send to section */}
            <div className="space-y-4 mt-6">
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
            </div>

            {/* Smart Send toggle */}
            <div className="flex items-center justify-between space-x-4 mt-6">
              <div className="space-y-0.5">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-96" />
              </div>
              <Skeleton className="h-6 w-10" />
            </div>
          </SectionCard>
        </div>

        {/* Right Column - Split Send */}
        <div>
          <SectionCard title="Split Send">
            <div className="space-y-4">
              <Skeleton className="h-4 w-64" />
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

export function DesignStepSkeleton() {
  return (
    <div className="mx-auto space-y-8">
      {/* Campaign header with variant selection */}
      <div className="space-y-6">
        <Skeleton className="h-6 w-24" /> {/* Variant title */}
        <div className="flex gap-3">
          {/* Variant buttons */}
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center">
              <Skeleton className="h-10 w-32 rounded-md" />
            </div>
          ))}
          {/* Follow-up options */}
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={`follow-${i}`} className="flex items-center">
              <Skeleton className="h-10 w-48 rounded-md" />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Left column - Messaging */}
        <div className="space-y-6">
          <SectionCard title="Messaging">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-24" /> {/* Label */}
                <Skeleton className="h-10 w-full rounded-md" /> {/* Input */}
                <Skeleton className="h-4 w-64" /> {/* Help text */}
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Message Content">
            <div className="min-h-[300px] rounded-md overflow-hidden bg-white">
              <Skeleton className="h-full w-full" /> {/* Editor area */}
            </div>
          </SectionCard>
        </div>

        {/* Right column - Preview */}
        <div className="space-y-8">
          <SectionCard title="Preview" className="h-full">
            <div className="flex flex-col h-full">
              {/* Estimates */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <Skeleton className="h-4 w-40" /> {/* Label */}
                  <Skeleton className="h-6 w-16" /> {/* Value */}
                </div>
                <div className="space-y-1">
                  <Skeleton className="h-4 w-32" /> {/* Label */}
                  <Skeleton className="h-6 w-16" /> {/* Value */}
                </div>
              </div>

              {/* Phone Preview */}
              <div className="flex-1 flex items-center justify-center py-8">
                <Skeleton className="h-[400px] w-[250px] rounded-xl" /> {/* Phone mockup */}
              </div>

              {/* Test Send Section */}
              <div className="flex justify-center">
                <Skeleton className="h-10 w-64 rounded-md" /> {/* Test send input */}
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
